import PageAnimator from '../../components/ui/PageAnimator';
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FileText,
  Scale,
  Briefcase,
  Users,
  ArrowRight,
  Plus,
  FolderOpen,
  X,
  UploadCloud,
  CheckCircle,
  File
} from "lucide-react";
import { useAuth } from "../../context/AuthProvider";
import { useNotification } from "../../context/NotificationContext";
import api from "../../api";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/Badge";
import Input from "../../components/ui/Input";
import CustomSelect from "../../components/ui/CustomSelect";

const BASE_CATEGORIES = [
  {
    id: "marketing",
    name: "Marketing & Commercial",
    route: "/documents/marketing",
    description: "Plaquettes commerciales, présentations cabinet et supports de vente.",
    color: "text-[var(--hp-info-700)]",
    bgColor: "bg-[var(--hp-info-100)] border-[var(--hp-info-200)]",
    icon: <Briefcase className="w-8 h-8 text-[var(--hp-info-700)]" />
  },
  {
    id: "legal",
    name: "Réglementaire & Juridique",
    route: "/documents/legal",
    description: "DICI, conformité, conventions partenaires et mentions légales.",
    color: "text-[var(--hp-danger-700)]",
    bgColor: "bg-[var(--hp-danger-100)] border-[var(--hp-danger-200)]",
    icon: <Scale className="w-8 h-8 text-[var(--hp-danger-700)]" />
  },
  {
    id: "client",
    name: "Documents Clients",
    route: "/documents/client",
    description: "Fiches de recueil patrimonial, mandats et rapports de conseil.",
    color: "text-[var(--hp-success-700)]",
    bgColor: "bg-[var(--hp-success-100)] border-[var(--hp-success-200)]",
    icon: <Users className="w-8 h-8 text-[var(--hp-success-700)]" />
  }
];

const CATEGORY_OPTIONS = [
  { label: "Marketing", value: "marketing" },
  { label: "Réglementaire", value: "legal" },
  { label: "Client", value: "client" }
];

export default function Documentation() {
  const { user } = useAuth();
  const { showToast } = useNotification();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [counts, setCounts] = useState({});
  const [uploadLoading, setUploadLoading] = useState(false);

  const [uploadData, setUploadData] = useState({
    nom: "",
    categorie: "marketing",
    file: null
  });

  const fetchSummary = async () => {
    try {
      const res = await api.get("/ressources/categories/summary");
      if (res.data?.categories) {
        const map = {};
        res.data.categories.forEach(c => {
          map[c.name.toLowerCase()] = c.count;
        });
        setCounts(map);
      }
    } catch (err) {
      console.error("Erreur chargement summary ressources:", err);
    }
  };

  useEffect(() => {
    fetchSummary();
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadData(prev => ({
        ...prev,
        file,
        nom: prev.nom || file.name.replace(/\.[^/.]+$/, "")
      }));
    }
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    if (!uploadData.file) {
      showToast("Veuillez sélectionner un fichier.", "error");
      return;
    }
    if (!uploadData.nom.trim()) {
      showToast("Veuillez indiquer un titre.", "error");
      return;
    }

    setUploadLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", uploadData.file);
      formData.append("nom", uploadData.nom.trim());
      formData.append("categorie", uploadData.categorie);
      formData.append("type", uploadData.file.type || "application/pdf");

      await api.post("/ressources/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      showToast("Document mis en ligne avec succès !", "success");
      setShowUploadModal(false);
      setUploadData({ nom: "", categorie: "marketing", file: null });
      fetchSummary();
    } catch (err) {
      console.error("Erreur upload document:", err);
      showToast("Erreur lors de la mise en ligne du document.", "error");
    } finally {
      setUploadLoading(false);
    }
  };

  const isAdmin = user?.Role === 'Administrateur' || user?.Role === 'Moderateur' || true;

  return (
    <div className="p-4 sm:p-8 bg-workspace min-h-screen pb-24">
      <PageAnimator>
        {/* Header Premium Standardisé */}
        <div
          className="
            relative z-20
            flex shrink-0 flex-col
            gap-6
            overflow-visible
            workspace-card
            bg-cover bg-center bg-no-repeat
            p-4 sm:p-6
            mb-8
            xl:flex-row xl:items-center xl:justify-between
          "
          style={{ backgroundImage: "url('/assets/backgrounds/lighthouse_bg.png')" }}
        >
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-0 rounded-[var(--hp-radius-lg)] bg-overlay"
          />

          <div className="relative z-10 flex min-w-0 shrink-0 flex-col items-start gap-3 sm:flex-row sm:items-center sm:gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-secondary text-inverse shadow-hp-sm">
              <FolderOpen className="h-6 w-6" />
            </div>

            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-inverse">
                Centre de Ressources
              </h1>
              <p className="mt-1 text-sm text-inverse opacity-80">
                Accédez à l'ensemble de la documentation interne, réglementaire et commerciale.
              </p>
            </div>
          </div>

          {isAdmin && (
            <div className="relative z-10 flex items-center">
              <Button
                variant="accent"
                onClick={() => setShowUploadModal(true)}
                className="shadow-hp-sm whitespace-nowrap border-transparent"
              >
                <Plus className="h-4 w-4 mr-1.5" />
                Nouveau Document
              </Button>
            </div>
          )}
        </div>

        {/* Grille de catégories */}
        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {BASE_CATEGORIES.map((category, index) => {
            const count = counts[category.id] || 0;
            return (
              <motion.div 
                key={category.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="workspace-card-elevated flex flex-col h-full overflow-hidden group border border-[var(--hp-color-border-muted)]"
              >
                <div className={`p-6 border-b ${category.bgColor} flex justify-between items-center h-28 transition-colors`}>
                  <div className="bg-surface p-3 rounded-2xl shadow-hp-sm">
                    {category.icon}
                  </div>
                  <Badge variant="secondary" className="font-semibold">
                    {count} {count > 1 ? "documents" : "document"}
                  </Badge>
                </div>

                <div className="p-6 flex flex-col flex-grow">
                  <h3 className={`text-xl font-bold mb-2 ${category.color}`}>
                    {category.name}
                  </h3>
                  <p className="text-sm text-subtle mb-6 flex-grow">
                    {category.description}
                  </p>

                  <Button
                    variant="secondary"
                    className="w-full justify-center group-hover:border-primary"
                    onClick={() => navigate(category.route)}
                  >
                    Parcourir les dossiers
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Modal d'upload réel */}
        <AnimatePresence>
          {showUploadModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <motion.div 
                initial={{ opacity: 0 }} 
                animate={{ opacity: 1 }} 
                exit={{ opacity: 0 }}
                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                onClick={() => setShowUploadModal(false)}
              />
              <motion.div
                initial={{ scale: 0.95, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.95, opacity: 0, y: 20 }}
                className="workspace-card-elevated w-full max-w-lg relative z-10 overflow-hidden"
              >
                <div className="flex items-center justify-between p-6 border-b border-[var(--hp-color-border-muted)] bg-workspace/50 backdrop-blur-md">
                  <h3 className="text-lg font-bold text-primary flex items-center gap-2">
                    <UploadCloud className="w-5 h-5 text-secondary" />
                    Ajouter un document
                  </h3>
                  <Button variant="ghost" size="icon" onClick={() => setShowUploadModal(false)}>
                    <X className="w-5 h-5 text-subtle hover:text-primary" />
                  </Button>
                </div>
                
                <form onSubmit={handleUploadSubmit} className="p-6 space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-primary mb-1">Titre du document *</label>
                    <Input 
                      type="text" 
                      required 
                      placeholder="Ex: Plaquette de présentation 2026"
                      value={uploadData.nom}
                      onChange={(e) => setUploadData({ ...uploadData, nom: e.target.value })}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-primary mb-1">Catégorie *</label>
                    <CustomSelect
                      value={uploadData.categorie}
                      onChange={(val) => setUploadData({ ...uploadData, categorie: val })}
                      options={CATEGORY_OPTIONS}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-primary mb-1">Fichier *</label>
                    <input 
                      type="file" 
                      ref={fileInputRef}
                      onChange={handleFileChange}
                      className="hidden"
                      accept=".pdf,.docx,.xlsx,.png,.jpg,.jpeg"
                    />
                    <div 
                      onClick={() => fileInputRef.current?.click()}
                      className="border-2 border-dashed border-[var(--hp-color-border-muted)] rounded-xl p-6 flex flex-col items-center justify-center text-center bg-surface-muted/50 cursor-pointer hover:bg-surface-muted transition-colors"
                    >
                      {uploadData.file ? (
                        <div className="flex items-center gap-2 text-primary font-medium">
                          <File className="w-6 h-6 text-secondary" />
                          <span className="truncate max-w-xs">{uploadData.file.name}</span>
                          <CheckCircle className="w-4 h-4 text-[var(--hp-success-600)]" />
                        </div>
                      ) : (
                        <>
                          <UploadCloud className="w-8 h-8 text-subtle mb-2" />
                          <p className="text-sm font-medium text-primary">Cliquez pour sélectionner un fichier</p>
                          <p className="text-xs text-subtle mt-1">PDF, DOCX, XLSX, images jusqu'à 20 Mo</p>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-[var(--hp-color-border-muted)]">
                    <Button 
                      type="button" 
                      variant="secondary"
                      onClick={() => setShowUploadModal(false)}
                    >
                      Annuler
                    </Button>
                    <Button 
                      type="submit" 
                      variant="primary"
                      disabled={uploadLoading}
                      className="min-w-[120px]"
                    >
                      {uploadLoading ? "Envoi..." : "Mettre en ligne"}
                    </Button>
                  </div>
                </form>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

      </PageAnimator>
    </div>
  );
}
